import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, FileText, Users, CreditCard, Ban, Scale, AlertTriangle, Mail, Shield } from 'lucide-react';

const LAST_UPDATED = 'December 2025';

const sections = [
  {
    icon: FileText,
    title: '1. Agreement',
    body: (
      <>
        <p>
          By creating a Monk Mode account or using the service, you agree to these Terms of
          Service and our Privacy Policy. If you don't agree, please don't use the service.
        </p>
        <p className="mt-3">
          Monk Mode is operated by its creators ("we", "us"). The service helps you set, track
          and keep personal goals.
        </p>
      </>
    ),
  },
  {
    icon: Users,
    title: '2. Your Account',
    body: (
      <ul className="list-disc pl-5 space-y-1">
        <li>You must be at least 13 years old (or the minimum age in your jurisdiction).</li>
        <li>You're responsible for keeping your password secure.</li>
        <li>One person per account. Don't share login credentials.</li>
        <li>You're responsible for all activity on your account.</li>
      </ul>
    ),
  },
  {
    icon: Shield,
    title: '3. Acceptable Use',
    body: (
      <>
        <p>Please don't:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Use the service for anything illegal or harmful.</li>
          <li>Attempt to access other users' accounts or data.</li>
          <li>Scrape, reverse-engineer, or disrupt the service.</li>
          <li>Spam, harass, or abuse other users in shared goals.</li>
          <li>Upload content that infringes someone else's rights.</li>
        </ul>
      </>
    ),
  },
  {
    icon: CreditCard,
    title: '4. Subscription & Billing',
    body: (
      <>
        <p>
          <span className="font-medium">Free tier:</span> Core goal tracking is free, forever.
        </p>
        <p className="mt-2">
          <span className="font-medium">Monk Mode Pro ($4.99/month):</span> Unlocks unlimited
          goals, shared goals with friends, the weekly tracker, goal groups, and priority support.
          Billing is handled by Stripe. Your subscription renews monthly until cancelled.
        </p>
        <p className="mt-3">
          You can cancel anytime from the "Manage Subscription" screen. Cancellation takes effect
          at the end of the current billing period. Payments already made are non-refundable
          except where required by law.
        </p>
      </>
    ),
  },
  {
    icon: Users,
    title: '5. Shared Goals',
    body: (
      <>
        <p>
          You may invite other users to join shared goals. Participants can see each other's
          progress on that goal. Only invite people you trust.
        </p>
        <p className="mt-3">
          We reserve the right to remove participants or shared goals that violate these terms
          or our community guidelines.
        </p>
      </>
    ),
  },
  {
    icon: Ban,
    title: '6. Account Deletion & Termination',
    body: (
      <>
        <p>
          You can delete your account at any time from the app, which removes your data and
          cancels any active subscription.
        </p>
        <p className="mt-3">
          We may suspend or terminate accounts that violate these terms, abuse the service, or
          create legal risk for us or other users. In serious cases we may terminate without notice.
        </p>
      </>
    ),
  },
  {
    icon: Scale,
    title: '7. Intellectual Property',
    body: (
      <p>
        Monk Mode's design, code, content and brand are ours. You can use the service as
        intended but can't copy, repackage, or build competing products from it. Content you
        create (your goals, notes, journal entries) stays yours — we only use it to run the service.
      </p>
    ),
  },
  {
    icon: AlertTriangle,
    title: '8. Disclaimers',
    body: (
      <p>
        Monk Mode is provided "as is." We don't guarantee uninterrupted or error-free operation.
        We're a tool to help you build habits — we're not a medical, legal, financial, or
        therapeutic service. Use your judgment, and consult a professional when needed.
      </p>
    ),
  },
  {
    icon: Scale,
    title: '9. Limitation of Liability',
    body: (
      <p>
        To the fullest extent allowed by law, we aren't liable for indirect, incidental, or
        consequential damages arising from your use of Monk Mode. Our total liability is capped
        at the amount you paid us in the past 12 months.
      </p>
    ),
  },
  {
    icon: FileText,
    title: '10. Changes to These Terms',
    body: (
      <p>
        We may update these terms occasionally. If the changes are material, we'll notify you
        in-app or by email. Continued use after a change means you accept the updated terms.
      </p>
    ),
  },
  {
    icon: Mail,
    title: '11. Contact',
    body: (
      <p>
        Questions about these terms? Email us at{' '}
        <a href="mailto:support@monkmode.app" className="underline">support@monkmode.app</a>.
      </p>
    ),
  },
];

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#1A1A1A]" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-6 py-10 sm:py-16">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 -ml-3 text-[#5F5F5F] hover:text-[#1A1A1A] hover:bg-transparent"
          data-testid="terms-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-4 border border-[#EEEAE0] bg-white text-[#5F5F5F]">
            <FileText className="w-3.5 h-3.5" />
            Last updated: {LAST_UPDATED}
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-[-0.03em] text-[#1A1A1A]">
            Terms of Service
          </h1>
          <p className="mt-3 text-base text-[#5F5F5F]">
            The ground rules for using Monk Mode.
          </p>
        </div>

        <Card className="border border-[#EEEAE0] bg-white shadow-sm">
          <CardContent className="p-6 sm:p-10 space-y-10">
            {sections.map((s, i) => {
              const Icon = s.icon;
              return (
                <section key={i} data-testid={`terms-section-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#FDF0EC] text-coral">
                      <Icon className="w-4 h-4" />
                    </div>
                    <h2 className="text-lg font-semibold text-[#1A1A1A]">{s.title}</h2>
                  </div>
                  <div className="text-sm leading-relaxed text-[#5F5F5F]">{s.body}</div>
                </section>
              );
            })}
          </CardContent>
        </Card>

        <p className="text-center text-xs mt-8 text-[#9E8778]">
          © {new Date().getFullYear()} Monk Mode. All rights reserved.
        </p>
      </div>
    </div>
  );
}
